import usePendingState from '../Hooks/usePendingState';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../Contexts/NotificationContext';

function FilterBar({ isVisible, setIsFilterOpen }) {
    const { setNotification } = useNotification();
    const [searchLoading, setSearchLoading, searchPendingRef] = usePendingState();
    const [searchQuery, setSearchQuery] = useState('');
    const [maxHeight, setMaxHeight] = useState('0px');
    const contentRef = useRef(null);
    const navigate = useNavigate();

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (searchPendingRef.current) return;
        setSearchLoading(true);

        const accessToken = localStorage.getItem('access_token');
        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            const response = await fetch(`${baseUrl}/painel/filtros/pesquisar?q=${encodeURIComponent(searchQuery)}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                navigate(`/painel/pesquisar?q=${encodeURIComponent(searchQuery)}`);
            } else {
                throw new Error('Erro ao buscar dados');
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        if (contentRef.current) {
            const height = contentRef.current.scrollHeight;
            setMaxHeight(isVisible ? `${height}px` : '0px');
        }
    }, [isVisible]);

    return (
        <div
            ref={contentRef}
            className="overflow-hidden bg-primary transition-all"
            style={{
                maxHeight: maxHeight,
            }}
        >
            <div className="container max-w-large">
                <div className="flex py-4 xl:py-7 items-center">
                    <div className="w-[20%]">
                        <button
                            className="flex gap-4 items-center text-white font-medium uppercase tracking-wider opacity-60 transition-all hover:opacity-80 max-lg:scale-90 origin-left"
                            onClick={() => setIsFilterOpen((prev) => !prev)}
                        >
                            <div className="px-3 py-2.5 border border-white rounded-lg">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="22.525"
                                    height="17.414"
                                    viewBox="0 0 22.525 17.414"
                                    className="fill-white"
                                >
                                    <g id="Camada_1-2" transform="translate(0.262 0.25)">
                                        <path
                                            id="Caminho_177"
                                            data-name="Caminho 177"
                                            d="M11.973,16.238a2.36,2.36,0,0,1-4.232.746c-.081-.126-.309-.728-.359-.746L.625,16.231c-.826-.051-.834-1.049,0-1.1l6.757-.007c.151-.265.227-.533.405-.791a2.33,2.33,0,0,1,4.155.752l.053.039,9.336,0c.856.014.905,1.043.044,1.105l-9.4.007ZM9.564,14.4a1.287,1.287,0,0,0,.216,2.564A1.287,1.287,0,0,0,9.564,14.4Z"
                                            transform="translate(0 -1.113)"
                                            strokeWidth="0.5"
                                        />
                                        <path
                                            id="Caminho_178"
                                            data-name="Caminho 178"
                                            d="M19.1,8.445c.094.06,1.909-.023,2.227,0a.749.749,0,0,1,.53.207.512.512,0,0,1-.3.854c-.348.041-2.365-.077-2.458,0a2.357,2.357,0,0,1-4.59-.007L.291,9.475C-.269,9.105.047,8.441.669,8.45l13.806,0a.113.113,0,0,0,.092-.045c.047-.057.2-.51.3-.669a2.346,2.346,0,0,1,4.241.708Zm-2.456-.718a1.291,1.291,0,0,0,.216,2.566A1.291,1.291,0,1,0,16.648,7.727Z"
                                            transform="translate(0 -0.556)"
                                            strokeWidth="0.5"
                                        />
                                        <path
                                            id="Caminho_179"
                                            data-name="Caminho 179"
                                            d="M8.655,1.774H21.331c.622-.009.938.656.377,1.025L8.768,2.834l-.093.045A2.341,2.341,0,0,1,4.9,4.122a3.405,3.405,0,0,1-.836-1.281L.358,2.825c-.616-.285-.376-1.033.267-1.047l3.515,0a2.324,2.324,0,0,1,4.515,0ZM6.25,1.052a1.277,1.277,0,1,0,.719,2.421A1.277,1.277,0,0,0,6.25,1.052Z"
                                            transform="translate(0 -0.001)"
                                            strokeWidth="0.5"
                                        />
                                    </g>
                                </svg>
                            </div>
                            <span className="max-md:hidden">Filtrar</span>
                        </button>
                    </div>

                    <div className="w-[80%]">
                        {searchLoading && <span role="status" className="text-white text-sm">Pesquisando...</span>}
                        <form className="relative ml-auto max-w-5xl" onSubmit={handleSearchSubmit}>
                            <input
                                className="text-sky-100 border border-sky-100 max-lg:text-sm border-opacity-30 w-full bg-transparent py-1.5 px-6 rounded-full"
                                placeholder="Pesquisa de arquivos"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                required
                            />
                            <button disabled={searchLoading} aria-busy={searchLoading} aria-label={searchLoading ? 'Pesquisando...' : 'Pesquisar'} type="submit" className="absolute top-2 right-6">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                                    <path
                                        d="M11.76,20.312a8.5,8.5,0,0,0,5.234-1.8l4.7,4.7L23.206,21.7l-4.7-4.7a8.542,8.542,0,1,0-6.747,3.316Zm0-14.967A6.414,6.414,0,1,1,5.345,11.76,6.421,6.421,0,0,1,11.76,5.345Z"
                                        transform="translate(-3.207 -3.207)"
                                        fill="#fff"
                                    />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FilterBar;
